/**
 * Basecamp icon-rail panel (ADR-0019).
 * Section nav grouped by workspace; opens main-pane tabs via integration-view-change.
 * Active row chrome matches Open Tabs / Browse (`ws-nav-item--active`) - maater-ui#harness.
 */

import { useMemo } from 'react'
import {
  Activity,
  Bookmark,
  Boxes,
  CheckSquare,
} from 'lucide-react'
import type { OpenTab } from '@/components/features/document-tabs'
import { useBasecampAuth } from '@/hooks/use-basecamp'
import { resolveBasecampModePanelSection, setBookmarksProjectFocus } from '@/lib/basecamp-tab-routing'
import { useWorkspace } from '@/providers/workspace-provider'
import { useWorkspaceExtensions } from '@/hooks/use-workspace-extensions'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { WorkspaceIcon } from '@/components/layout/left-sidebar.components'
import { ModePanelNavItem } from '@/components/layout/sidebar/ModePanelNavItem'
import { wsNavSectionStyle } from '@/lib/ws-nav'

const SECTIONS = [
  { id: 'bookmarks', label: 'Bookmarks', icon: Bookmark, tabId: 'basecamp-bookmarks', title: 'Bookmarks' },
  { id: 'todos', label: 'Todos', icon: CheckSquare, tabId: 'basecamp-todos', title: 'Todos' },
  { id: 'projects', label: 'Projects', icon: Boxes, tabId: 'basecamp-projects', title: 'Projects' },
  { id: 'activity', label: 'Activity', icon: Activity, tabId: 'basecamp-activity', title: 'Activity' },
] as const

function BasecampWorkspaceBlock({
  workspaceId,
  workspaceName,
  workspaceColor,
  workspaceIcon,
  dense,
  activeSectionId,
  openSectionIds = [],
}: {
  workspaceId: string
  workspaceName: string
  workspaceColor?: string | null
  workspaceIcon?: string | null
  dense?: boolean
  /** Section to highlight when the focused tab belongs to this workspace. */
  activeSectionId?: string | null
  /** Sections with an open tab that is not focused (working-set echo). */
  openSectionIds?: string[]
}) {
  useBasecampAuth(workspaceId)
  const color = workspaceColor ?? 'hsl(var(--primary))'

  const openSection = (section: (typeof SECTIONS)[number]) => {
    if (section.id === 'bookmarks') setBookmarksProjectFocus(null)
    window.dispatchEvent(
      new CustomEvent('integration-view-change', {
        detail: {
          tabId: section.tabId,
          title: section.title,
          type: 'integration-basecamp',
          integrationData: {
            integrationId: 'basecamp',
            section: section.id,
            organizationId: workspaceId,
          },
        },
      }),
    )
  }

  return (
    <div
      className="ws-nav-section min-w-0 border-b border-border/60 last:border-b-0"
      style={wsNavSectionStyle(workspaceColor)}
      data-testid={testIds.basecampSidebar.workspaceBlock(workspaceId)}
    >
      <div className={cn('flex items-center gap-2 px-3', dense ? 'py-1.5' : 'py-2')}>
        <span
          className="flex h-6 w-6 flex-none items-center justify-center rounded"
          style={{ backgroundColor: color + '30' }}
        >
          <WorkspaceIcon icon={workspaceIcon ?? null} color={color} name={workspaceName} size={12} />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{workspaceName}</span>
      </div>
      <div className="pb-1">
        {SECTIONS.map((section) => {
          const Icon = section.icon
          const isActive = activeSectionId === section.id
          const isOpen = !isActive && openSectionIds.includes(section.id)
          return (
            <ModePanelNavItem
              key={section.id}
              active={isActive}
              open={isOpen}
              dense={dense}
              onClick={() => openSection(section)}
              data-testid={testIds.basecampSidebar.section(workspaceId, section.id)}
              icon={
                <Icon
                  className={cn(
                    'h-3.5 w-3.5 flex-none',
                    isActive ? 'text-sidebar-foreground' : 'text-muted-foreground',
                  )}
                />
              }
              label={section.label}
            />
          )
        })}
      </div>
    </div>
  )
}

function WorkspaceBasecampGate({
  workspaceId,
  children,
}: {
  workspaceId: string
  children: (enabled: boolean, loading: boolean) => React.ReactNode
}) {
  const { isExtensionEnabled, isLoading } = useWorkspaceExtensions(workspaceId)
  return <>{children(isExtensionEnabled('basecamp'), isLoading)}</>
}

export function BasecampSidebarPanel({
  dense,
  organizationIds,
  activeTabId = null,
  openTabs = [],
}: {
  dense?: boolean
  organizationIds: string[]
  /** Focused main-pane tab id - keeps mode-panel section in sync with Open Tabs. */
  activeTabId?: string | null
  openTabs?: OpenTab[]
}) {
  const { workspaces } = useWorkspace()
  const candidates = useMemo(
    () =>
      organizationIds
        .map((id) => workspaces.find((w) => w.id === id))
        .filter((w): w is NonNullable<typeof w> => Boolean(w)),
    [organizationIds, workspaces],
  )

  const activeTab = useMemo(
    () => (activeTabId ? openTabs.find((t) => t.id === activeTabId) ?? null : null),
    [activeTabId, openTabs],
  )

  const activeMode = useMemo(() => {
    const fromTab = resolveBasecampModePanelSection(activeTab)
    if (fromTab) return fromTab
    // Focused id without a hydrated openTabs entry (URL restore race)
    if (!activeTabId) return null
    return resolveBasecampModePanelSection({
      id: activeTabId,
      title: '',
      type: 'integration-basecamp',
    })
  }, [activeTab, activeTabId])

  /** Per-workspace sections that have an open (non-focused) Basecamp tab. */
  const openSectionsByWorkspace = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const tab of openTabs) {
      if (activeTabId && tab.id === activeTabId) continue
      const mode = resolveBasecampModePanelSection(tab)
      if (!mode?.sectionId) continue
      const orgId =
        mode.organizationId ??
        tab.integrationData?.organizationId ??
        tab.workspaceId ??
        null
      if (!orgId) continue
      const set = map.get(orgId) ?? new Set<string>()
      set.add(mode.sectionId)
      map.set(orgId, set)
    }
    return map
  }, [openTabs, activeTabId])

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto"
      data-testid={testIds.basecampSidebar.container}
    >
      {candidates.length === 0 ? (
        <p className="px-3 py-4 text-xs text-muted-foreground">No active workspaces.</p>
      ) : (
        candidates.map((ws) => {
          const orgMatches =
            activeMode?.organizationId != null
              ? activeMode.organizationId === ws.id
              : candidates.length === 1
          const sectionForWs =
            activeMode && orgMatches ? activeMode.sectionId : null
          const openSectionIds = [...(openSectionsByWorkspace.get(ws.id) ?? [])]
          return (
            <WorkspaceBasecampGate key={ws.id} workspaceId={ws.id}>
              {(enabled, loading) => {
                if (loading || !enabled) return null
                return (
                  <BasecampWorkspaceBlock
                    workspaceId={ws.id}
                    workspaceName={ws.name}
                    workspaceColor={ws.color}
                    workspaceIcon={ws.icon}
                    dense={dense}
                    activeSectionId={sectionForWs}
                    openSectionIds={openSectionIds}
                  />
                )
              }}
            </WorkspaceBasecampGate>
          )
        })
      )}
    </div>
  )
}
